import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ToolErrorRow } from "@/features/admin";
import { useState } from "react";
import { Link } from "react-router-dom";
import { when } from "../format";

/** Characters of the error text shown before "Voir tout". */
const PREVIEW = 220;

/**
 * The failed calls themselves, newest first: what the agent read back (`error`) next to
 * what it had sent (`input`, redacted server-side), with the badges that tell an incident
 * from an instruction, a wrap-up call from a live one, and an error the agent recovered from.
 */
export function ErrorList({
  rows,
  total,
  offset,
  pageSize,
  isLoading,
  onPage,
  tool,
}: {
  rows: ToolErrorRow[];
  total: number;
  offset: number;
  pageSize: number;
  isLoading?: boolean;
  onPage: (offset: number) => void;
  tool?: string;
}) {
  const from = total ? offset + 1 : 0;
  const to = Math.min(offset + rows.length, total);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm">
            Dernières erreurs{tool ? ` — ${tool}` : ""}
          </CardTitle>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {from}–{to} sur {total}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0 || isLoading}
              onClick={() => onPage(Math.max(0, offset - pageSize))}
            >
              ‹
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + pageSize >= total || isLoading}
              onClick={() => onPage(offset + pageSize)}
            >
              ›
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Les coordonnées et secrets présents dans les paramètres sont masqués.
        </p>
      </CardHeader>
      <CardContent>
        {isLoading && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucune erreur sur la période.
          </p>
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <ErrorItem key={row.id} row={row} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ErrorItem({ row }: { row: ToolErrorRow }) {
  const [expanded, setExpanded] = useState(false);
  const long = row.error.length > PREVIEW || row.error_truncated;
  const text =
    expanded || !long ? row.error : `${row.error.slice(0, PREVIEW)}…`;
  const input =
    typeof row.input === "string"
      ? row.input
      : JSON.stringify(row.input, null, 2);
  const hasInput = input && input !== "{}";

  return (
    <li className="py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground whitespace-nowrap tabular-nums">
          {when(row.created_at)}
        </span>
        <Link
          to={`/admin/clients/${row.client_id}`}
          className="font-medium hover:underline"
        >
          {row.client_name || row.client_id}
        </Link>
        <span className="font-mono text-xs">{row.tool || "—"}</span>
        {row.incident ? (
          <Badge color="red">Incident</Badge>
        ) : (
          <Badge color="gray">Instruction</Badge>
        )}
        {row.wrapup ? <Badge color="purple">Wrap-up</Badge> : null}
        {row.made_good ? <Badge color="green">Rattrapé</Badge> : null}
        <Link
          to={`/${row.client_id}/calls/session/${row.session_id}`}
          className="ml-auto text-primary hover:underline whitespace-nowrap text-xs"
        >
          Voir la conversation
        </Link>
      </div>

      <div className="text-sm whitespace-pre-wrap break-words">
        {text || <span className="text-muted-foreground">(message vide)</span>}
        {long ? (
          <Button
            variant="link"
            size="sm"
            className="h-auto px-1 py-0 text-xs"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Réduire" : "Voir tout"}
          </Button>
        ) : null}
        {expanded && row.error_truncated ? (
          <span className="block text-xs text-muted-foreground">
            (tronqué à 2000 caractères)
          </span>
        ) : null}
      </div>

      {hasInput ? (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground select-none">
            Paramètres
          </summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded-md bg-muted p-2 font-mono whitespace-pre-wrap break-all">
            {input}
          </pre>
        </details>
      ) : null}
    </li>
  );
}
